// src/pages/modules/Admin/RoleManager/RoleManager.tsx
import React from 'react'
import {
  Alert, Badge, Button, Col, Form,
  Modal, Row, Spinner, Table,
} from 'react-bootstrap'
import { AuthContext, dbRead, dbSet, dbUpdate } from '../../../../_services/firebase'
import { DB_ADMIN, DB_ADMIN_BY_ID } from '../../../../_config/db_paths'
import { saveLog } from '../../../../_services/firebase'
import { formatDate, toastDanger, toastSuccess } from '../../../../_utils'
import ConfirmAlert from '../../../../components/ConfirmAlert/ConfirmAlert'
import type { AdminRecord, UserRole, sortBy } from '../../../../_types'

const ROLE_OPTIONS: { value: UserRole | 'expire'; label: string }[] = [
  { value: 'super admin', label: 'ผู้ดูแลระบบ' },
  { value: 'ceohead', label: 'หุ้นส่วน' },
  { value: 'acchead', label: 'หัวหน้าบัญชี' },
  { value: 'account', label: 'พนักงานบัญชี' },
  { value: 'debhead', label: 'หัวหน้าเร่งรัดหนี้' },
  { value: 'debadmn', label: 'แอดมินเร่งรัดหนี้' },
  { value: 'debfllw', label: 'พนักงานติดตามหนี้' },
  { value: 'makhead', label: 'หัวหน้าการตลาด'},
  { value: 'marketg', label: 'การตลาด'},
  { value: 'expire', label: 'สิ้นสุดการเป็นพนักงาน' }
]

const SORT_OPTIONS: { value: sortBy; label: string }[] = [
  { value: 'name', label: 'ชื่อ' },
  { value: 'email', label: 'อีเมล์' },
  { value: 'role', label: 'ตำแหน่ง' },
  { value: 'lastupdate', label: 'อัพเดตล่าสุด' }
]

const ROLE_BADGE: Record<string, string> = {
  'super admin': 'danger',
  ceohead: 'dark',
  acchead: 'primary',
  account: 'info',
  debhead: 'warning',
  debadmn: 'secondary',
  debfllw: 'light',
}

export default function RoleManager() {
  const { currentUser } = React.useContext(AuthContext)
  const myUid = currentUser?.uid || ''

  const [loading, setLoading] = React.useState(true)
  const [employees, setEmployees] = React.useState<AdminRecord[]>([])
  const [search, setSearch] = React.useState('')
  const [roleFilter, setRoleFilter] = React.useState('all')
  const [sortby, setSortby] = React.useState('name')

  // Add modal
  const [showAdd, setShowAdd] = React.useState(false)
  const [addUid, setAddUid] = React.useState('')
  const [addName, setAddName] = React.useState('')
  const [addEmail, setAddEmail] = React.useState('')
  const [addRole, setAddRole] = React.useState<UserRole>('account')
  const [addSaving, setAddSaving] = React.useState(false)

  // Edit modal
  const [showEdit, setShowEdit] = React.useState(false)
  const [editTarget, setEditTarget] = React.useState<AdminRecord | null>(null)
  const [editRole, setEditRole] = React.useState<UserRole>('account')
  const [editName, setEditName] = React.useState('')
  const [editSaving, setEditSaving] = React.useState(false)

  const [confirmShow, setConfirmShow] = React.useState(false)

  const loadData = React.useCallback(async () => {
    setLoading(true)
    try {
      const raw = await dbRead<Record<string, AdminRecord>>(DB_ADMIN)
      const list = Object.entries(raw || {}).map(([k, v]) => ({ ...v, key: v.key || k }))
        .filter((e) => e.key && e.email)
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
      setEmployees(list)
    } catch (e: any) {
      toastDanger(e?.message || 'โหลดไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => { loadData() }, [loadData])

  // อันดับตำแหน่ง (ใช้ตอนเรียงตาม role ให้เรียงตามลำดับชั้น ไม่ใช่ตามตัวอักษร)
  const roleRank = (r: string) => {
    const i = ROLE_OPTIONS.findIndex((o) => o.value === r)
    return i === -1 ? 99 : i
  }

  const filtered = employees
    .filter((e) => {
      const matchSearch = !search ||
        (e.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (e.email || '').toLowerCase().includes(search.toLowerCase()) ||
        e.key.includes(search)
      const matchRole = roleFilter === 'all' || e.role === roleFilter
      return matchSearch && matchRole
    })
    // จัดเรียงตามตัวเลือก sortby
    .sort((a, b) => {
      switch (sortby) {
        case 'email': return (a.email || '').localeCompare(b.email || '')        // อีเมล A→Z
        case 'role': return roleRank(a.role) - roleRank(b.role)                  // ตำแหน่งตามลำดับชั้น
        case 'lastupdate': return (b.updatedAt || 0) - (a.updatedAt || 0)              // อัพเดตล่าสุดก่อน
        case 'name':
        default: return (a.name || '').localeCompare(b.name || '', 'th')     // ชื่อ ก→ฮ
      }
    })

  const handleAdd = async () => {
    if (!addUid.trim() || !addEmail.trim() || !addRole) { toastDanger('กรุณากรอกข้อมูลให้ครบ'); return }
    setAddSaving(true)
    try {
      const rec: AdminRecord = {
        key: addUid.trim(),
        name: addName.trim(),
        email: addEmail.trim(),
        role: addRole,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      }
      await dbSet(DB_ADMIN_BY_ID(addUid.trim()), rec)
      await saveLog(myUid, 'create', addUid.trim(), `เพิ่มพนักงาน ${addName} role: ${addRole}`)
      toastSuccess(`เพิ่มพนักงาน ${addName} เรียบร้อย`)
      setShowAdd(false)
      setAddUid(''); setAddName(''); setAddEmail(''); setAddRole('account')
      await loadData()
    } catch (e: any) {
      toastDanger(e?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setAddSaving(false)
    }
  }

  const openEdit = (emp: AdminRecord) => {
    setEditTarget(emp)
    setEditRole(emp.role)
    setEditName(emp.name || '')
    setShowEdit(true)
  }

  const handleEdit = async () => {
    if (!editTarget) return
    setEditSaving(true)
    try {
      await dbUpdate(DB_ADMIN_BY_ID(editTarget.key), {
        name: editName,
        role: editRole,
        updatedAt: Date.now(),
      })
      await saveLog(myUid, 'update', editTarget.key, `เปลี่ยน role เป็น ${editRole}`)
      toastSuccess('อัปเดตเรียบร้อย')
      setShowEdit(false)
      setEditTarget(null)
      await loadData()
    } catch (e: any) {
      toastDanger(e?.message || 'บันทึกไม่สำเร็จ')
    } finally {
      setEditSaving(false)
      setConfirmShow(false)
    }
  }

  if (loading) return (
    <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 300 }}>
      <Spinner animation='border' variant='warning' />
    </div>
  )

  return (
    <div className='p-3'>
      <ConfirmAlert
        show={confirmShow}
        title='ยืนยันการเปลี่ยน Role'
        message={`เปลี่ยน role ของ ${editTarget?.name || editTarget?.email} เป็น "${ROLE_OPTIONS.find((r) => r.value === editRole)?.label}"?`}
        confirmLabel={editSaving ? 'กำลังบันทึก...' : 'ยืนยัน'}
        variant='warning'
        onConfirm={handleEdit}
        onCancel={() => setConfirmShow(false)}
      />

      {/* Header */}
      <div className='d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2'>
        <div>
          <h5 className='mb-0'>จัดการพนักงาน</h5>
          <div className='text-muted' style={{ fontSize: 12 }}>พนักงานทั้งหมด {employees.length} คน</div>
        </div>
        <Button variant='warning' size='sm' onClick={() => setShowAdd(true)}>
          <i className='fa-solid fa-plus me-1' />เพิ่มพนักงาน
        </Button>
      </div>

      {/* Filter */}
      <Row className='g-2 mb-3'>
        <Col xs={12} md={6}>
          <Form.Control size='sm' placeholder='ค้นหา ชื่อ / อีเมล / uid' value={search} onChange={(e) => setSearch(e.target.value)} />
        </Col>
        <Col xs={8} md={4}>
          <Form.Select size='sm' value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <option value='all'>ทุก Role</option>
            {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </Form.Select>
        </Col>
        <Col xs={2} md={1}>
          <Button size='sm' variant='outline-danger' className='w-100' onClick={() => { setSearch(''); setRoleFilter('all') }}>ล้าง</Button>
        </Col>
        <Col xs={2} md={1}>
          <Form.Select size='sm' value={sortby} onChange={(e) => setSortby(e.target.value)}>
            {SORT_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </Form.Select>
        </Col>
      </Row>

      {filtered.length === 0 && <Alert variant='info' style={{ fontSize: 13 }}>ไม่พบพนักงาน</Alert>}

      <Table size='sm' bordered hover style={{ fontSize: 13 }}>
        <thead className='table-light'>
          <tr>
            <th>ชื่อ</th>
            <th>อีเมล</th>
            <th>UID</th>
            <th>Role</th>
            <th>อัปเดต</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {filtered
            .filter((emp) => emp.key !== "2kz0tCEE3OYsSQXTf61ptaCDbDr2")
            .map((emp) => (
              <tr key={emp.key}>
                <td style={{ fontWeight: 600 }}>{emp.name || "-"}</td>
                <td>{emp.email || "-"}</td>
                <td style={{ fontSize: 10, color: "#888", fontFamily: "monospace" }}>
                  {emp.key}
                </td>
                <td>
                  <Badge
                    bg={ROLE_BADGE[emp.role] || "secondary"}
                    text={emp.role === "debfllw" ? "dark" : undefined}
                    style={{ fontSize: 11 }}
                  >
                    {ROLE_OPTIONS.find((r) => r.value === emp.role)?.label || emp.role}
                  </Badge>
                </td>
                <td style={{ fontSize: 11, color: "#888" }}>
                  {emp.updatedAt ? formatDate(emp.updatedAt) : "-"}
                </td>
                <td>
                  <Button
                    size="sm"
                    variant="outline-warning"
                    style={{ fontSize: 11 }}
                    onClick={() => openEdit(emp)}
                  >
                    แก้ไข
                  </Button>
                </td>
              </tr>
            ))}
        </tbody>
      </Table>

      {/* Add Modal */}
      <Modal show={showAdd} onHide={() => setShowAdd(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 15 }}>เพิ่มพนักงานใหม่</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className='g-3'>
            <Col xs={12}>
              <Form.Group>
                <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>UID (Firebase Auth UID) *</Form.Label>
                <Form.Control size='sm' value={addUid} onChange={(e) => setAddUid(e.target.value)} placeholder='Firebase UID' />
                <Form.Text className='text-muted'>UID ของผู้ใช้จาก Firebase Authentication</Form.Text>
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>ชื่อ-นามสกุล *</Form.Label>
                <Form.Control size='sm' value={addName} onChange={(e) => setAddName(e.target.value)} />
              </Form.Group>
            </Col>
            <Col xs={12} md={6}>
              <Form.Group>
                <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>อีเมล *</Form.Label>
                <Form.Control size='sm' type='email' value={addEmail} onChange={(e) => setAddEmail(e.target.value)} />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Role *</Form.Label>
                <Form.Select size='sm' value={addRole} onChange={(e) => setAddRole(e.target.value as UserRole)}>
                  {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='outline-secondary' size='sm' onClick={() => setShowAdd(false)}>ยกเลิก</Button>
          <Button variant='warning' size='sm' disabled={addSaving} onClick={handleAdd}>
            {addSaving ? <Spinner size='sm' animation='border' /> : <><i className='fa-solid fa-plus me-1' />เพิ่ม</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal show={showEdit} onHide={() => setShowEdit(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 15 }}>แก้ไขพนักงาน — {editTarget?.name || editTarget?.email}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className='g-3'>
            <Col xs={12}>
              <Form.Group>
                <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>ชื่อ-นามสกุล</Form.Label>
                <Form.Control size='sm' value={editName} onChange={(e) => setEditName(e.target.value)} />
              </Form.Group>
            </Col>
            <Col xs={12}>
              <Form.Group>
                <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Role</Form.Label>
                <Form.Select size='sm' value={editRole} onChange={(e) => setEditRole(e.target.value as UserRole)}>
                  {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col xs={12}>
              <div style={{ fontSize: 12, color: '#888' }}>
                UID: <code>{editTarget?.key}</code><br />
                Email: {editTarget?.email}
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='outline-secondary' size='sm' onClick={() => setShowEdit(false)}>ยกเลิก</Button>
          <Button variant='warning' size='sm' disabled={editSaving} onClick={() => setConfirmShow(true)}>
            บันทึก
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}